import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import { getScoreInterpretationLabel, getScore } from '../../../utils/shared/cellUtils';
import { GATING_MAP, CELL_TYPE } from '../../../constants/shared/CellConstants';
import { INTERP } from '../../../constants/shared/CommonConstants';
import { isTrcPresumedResult } from '../../../utils/shared/trcUtils';
import { CommonScoreCell, InputCell, TextCell } from './Cells';
import './ScoreCell.scss';

const { INPUT, BENCHMARK_GATING_MEASURE, SS_GATING_MEASURE } = CELL_TYPE;
const { BLANK } = INTERP;

export const ScoreCell = props => {
    const { isCellDisabled, interp, cellType, proficientLevel, getCustomScore } = props;
    const scoreInterpretationLabel = getScoreInterpretationLabel(interp, proficientLevel);

    switch (cellType) {
        case CELL_TYPE.STUDENT_SUMMARY:
            return (
                <StudentSummaryScoreCell {...props}>
                    <>
                        {!isCellDisabled && <div className="ss-score">{getCustomScore(props)}</div>}
                        {scoreInterpretationLabel && interp !== BLANK && (
                            <div className="ss-interp">{scoreInterpretationLabel}</div>
                        )}
                    </>
                </StudentSummaryScoreCell>
            );
        case INPUT:
            return <InputCell {...props} />;
        default:
            return <CommonScoreCell {...props} />;
    }
};

ScoreCell.defaultProps = {
    cellType: null,
    isCellDisabled: false,
    interp: '',
    gatingType: '',
    optionalCellData: {},
    proficientLevel: '',
    getCustomScore: getScore,
};

ScoreCell.propTypes = {
    cellType: PropTypes.string,
    isCellDisabled: PropTypes.bool,
    interp: PropTypes.string,
    gatingType: PropTypes.string,
    proficientLevel: PropTypes.string,
    optionalCellData: PropTypes.object,
    getCustomScore: PropTypes.func,
};

export const StudentSummaryScoreCell = props => {
    const { optionalCellData, onScoreClick, interp, isCellDisabled, score, measure, goalSection } =
        props;
    const { isAdditionalPanel, classNames, isMeasureAvailable, hasBottomSpace } = optionalCellData;
    const hoverClass =
        isAdditionalPanel || isTrcPresumedResult(measure, score) ? 'without-hover' : '';
    const emptyCellClass = isCellDisabled ? 'empty-cell' : '';

    return (
        <div className={`data-cell ss ${classNames}`} onClick={onScoreClick} role="presentation">
            {isMeasureAvailable && (
                <>
                    <div className="ss-score-goal">{goalSection}</div>
                    <div className={`ss-score-container ${interp} ${hoverClass} ${emptyCellClass}`}>
                        {props.children}
                    </div>
                    {hasBottomSpace && <div className="ss-bottom" />}
                </>
            )}
        </div>
    );
};

StudentSummaryScoreCell.defaultProps = {
    interp: null,
    score: null,
    onScoreClick: () => {},
    optionalCellData: {},
    isCellDisabled: false,
    measure: '',
    children: {},
    goalSection: '',
};

StudentSummaryScoreCell.propTypes = {
    onScoreClick: PropTypes.func,
    optionalCellData: PropTypes.object,
    interp: PropTypes.string,
    isCellDisabled: PropTypes.bool,
    score: PropTypes.oneOfType([PropTypes.number, PropTypes.object, PropTypes.string]),
    measure: PropTypes.string,
    children: PropTypes.object,
    goalSection: PropTypes.string,
};

const SharedScoreCell = props => {
    const {
        cellType,
        statusForMeasureYearPeriod,
        measure,
        onScoreClick,
        onEmptyScoreClick,
        gatingMeasureCellType,
        optionalCellData,
        sortIndex,
        isCellDisabled,
        hasLicense,
        goalSection,
        measuresWithPercentage,
        measuresNotCompleted,
        getCustomScore,
        isSecure,
    } = props;
    const { interp, score, isComplete, isUnofficial, gatingType, percentile, proficientLevel } =
        statusForMeasureYearPeriod;
    const cellTypeCommon = isEmpty(gatingType) ? cellType : gatingMeasureCellType;
    const commonProps = {
        cellType: cellTypeCommon,
        optionalCellData,
        sortIndex,
        goalSection,
        measuresWithPercentage,
        measuresNotCompleted,
    };

    if (!hasLicense) {
        return <TextCell />;
    }

    const cellProps = isEmpty(statusForMeasureYearPeriod)
        ? {
              ...commonProps,
              onScoreClick: onEmptyScoreClick,
              interp: BLANK,
              getCustomScore,
              isCellDisabled,
              isSecure,
          }
        : {
              ...commonProps,
              measure,
              interp,
              score,
              isComplete,
              isUnofficial,
              onScoreClick,
              gatingType,
              percentile,
              proficientLevel,
              getCustomScore,
              isSecure,
          };
    switch (cellTypeCommon) {
        case BENCHMARK_GATING_MEASURE: {
            return (
                <TextCell
                    content={GATING_MAP[gatingType]}
                    classNames={`gating-cell transparent-gradient ${interp} without-hover ${optionalCellData.classNames}`}
                />
            );
        }
        case SS_GATING_MEASURE:
            return (
                <StudentSummaryScoreCell {...cellProps}>
                    <TextCell
                        content={GATING_MAP[gatingType]}
                        classNames={`gating-cell data-cell transparent-gradient ${interp} without-hover ${optionalCellData.classNames}`}
                    />
                </StudentSummaryScoreCell>
            );
        default:
            return <ScoreCell {...cellProps} />;
    }
};

SharedScoreCell.defaultProps = {
    cellType: null,
    statusForMeasureYearPeriod: {},
    measure: null,
    goalSection: '',
    onScoreClick: () => {},
    onEmptyScoreClick: () => {},
    gatingMeasureCellType: null,
    optionalCellData: {},
    sortIndex: {},
    hasLicense: true,
    isCellDisabled: false,
    measuresWithPercentage: [],
    measuresNotCompleted: [],
    getCustomScore: getScore,
    isSecure: false,
};

SharedScoreCell.propTypes = {
    cellType: PropTypes.string,
    statusForMeasureYearPeriod: PropTypes.object,
    measure: PropTypes.string,
    goalSection: PropTypes.string,
    onScoreClick: PropTypes.func,
    onEmptyScoreClick: PropTypes.func,
    gatingMeasureCellType: PropTypes.string,
    optionalCellData: PropTypes.object,
    sortIndex: PropTypes.object,
    isCellDisabled: PropTypes.bool,
    hasLicense: PropTypes.bool,
    measuresWithPercentage: PropTypes.array,
    measuresNotCompleted: PropTypes.array,
    getCustomScore: PropTypes.func,
    isSecure: PropTypes.bool,
};

export default SharedScoreCell;
